import { buildTenantCapabilities } from '@/lib/tenant-capabilities.mjs';
import { useQuery } from '@tanstack/react-query';
import { tenantMetaApi } from '@/api/supports';
import { useTenantContext } from './useTenantContext';
import { useAuthStore } from '@/store/authStore';

/**
 * Loads the current tenant's enabled modules (from GET /tenants/meta) and
 * exposes a helper to decide whether a module's tenant-admin surfaces should
 * be shown. A SuperAdmin on the platform menu (not impersonating) bypasses
 * gating entirely. When a SuperAdmin impersonates a tenant, we fetch and gate
 * by THAT tenant's modules (apiClient sends X-Impersonate-Tenant), so the menu
 * mirrors what the tenant admin sees. Lookup errors fail closed: an
 * entitlement outage must never expose a disabled tenant surface.
 */
export function useEnabledModules() {
  const { isSuperAdmin, impersonatedTenantId } = useTenantContext();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const role = useAuthStore((state) => state.role);
  const isImpersonating = isSuperAdmin && impersonatedTenantId != null;
  // SuperAdmin only bypasses gating while on the platform menu (no tenant).
  const bypassGating = isSuperAdmin && !isImpersonating;

  const { data, isLoading, isError } = useQuery({
    // Key by the impersonated tenant so switching tenants refetches its modules.
    queryKey: ['tenant-meta-modules', impersonatedTenantId ?? null],
    queryFn: tenantMetaApi.getMeta,
    staleTime: 5 * 60 * 1000,
    retry: 1,
    // During initial hydration the role is still null and the context helper
    // deliberately defaults to tenant mode. Without this readiness gate a
    // global SuperAdmin briefly calls /tenants/meta and receives a misleading
    // 404 before the persisted role is restored.
    enabled: isAuthenticated && (
      role === 'tenantadmin'
      || role === 'provideradmin'
      || role === 'providerstaff'
      || role === 'support'
      || (role === 'superadmin' && impersonatedTenantId != null)
    ),
  });

  const enabledModules = data?.enabledModules ?? [];
  const moduleConfigs = data?.moduleConfigs ?? {};

  /**
   * A module is manageable in the tenant portal when it's enabled AND its
   * `tenantPortal` channel isn't explicitly turned off by SuperAdmin
   * (default true). Platform-menu SuperAdmin and lookup errors bypass the gate.
   */
  const isManageable = (moduleKey: string): boolean => {
    if (bypassGating) return true;
    if (isError || !data) return false;
    if (!enabledModules.includes(moduleKey)) return false;
    return moduleConfigs[moduleKey]?.channels?.tenantPortal === true;
  };

  const providerAdminAllowed = bypassGating || (
    !isError && !!data && data.channels?.providerAdminPortal === true
  );

  return {
    capabilities: buildTenantCapabilities(data),
    enabledModules,
    moduleConfigs,
    isManageable,
    providerAdminAllowed,
    isLoading,
    isError,
    // True once we have a definitive answer (data arrived, errored, or N/A).
    ready: bypassGating || isError || !!data,
  };
}
